import React, { createContext, useContext, useEffect, useTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Permission } from 'src/contstant/permissionAccessConfig';
import { APP_PROGRAM_ID } from 'src/contstant/commonLabels';
import useMidway from 'src/hooks/useMidway';
import routePaths from 'src/contstant/routePaths';
import AILoadingScreen from 'src/components/AILoadingScreen';
import {
  PermissionsExpiredException,
  ProfileSettings,
  UserMetadataV2,
  UserPermissionV2,
  UserPreferencesDetailsV2,
} from '@amzn/gbsa-orbit-typescript-client';
import {
  useUserAlias,
  useUserDetailsQuery,
  useUserMetadataQuery,
  useUserPermissionsQuery,
  useUserProgramIdQuery,
} from 'src/data/api/orbit-service-ts/user/queries';
import { useUserPreferencesQuery } from 'src/data/api/orbit-service-ts/user-preferences/queries';
import { Flashbar } from '@cloudscape-design/components';

const initMessages = ['Loading your workspace', 'Fetching preferences', 'Almost ready'];
interface PermissionContextValue {
  permissions: Set<Permission>;
  user: UserPermissionV2;
  userPreferences: Omit<UserPreferencesDetailsV2, 'profileSettings'> & {
    profileSettings: ProfileSettings & {
      businessUnit: string;
      businessUnitLocationPreferences: string[];
    };
  };
  userMetadata: UserMetadataV2;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoading: isMidwayLoading, error: midwayError } = useMidway();
  const [userAlias] = useUserAlias();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const location = useLocation();

  const { data: programId } = useUserProgramIdQuery({ programId: APP_PROGRAM_ID });

  // User preferences query - only fetch when userAlias and programId are available
  const {
    data: userPreferences,
    isLoading: isUserPreferencesLoading,
    error: userPreferencesError,
  } = useUserPreferencesQuery({
    userId: userAlias,
    programId,
  });
  const businessUnit = userPreferences?.profileSettings?.businessUnit;

  const {
    data: userMetadata,
    error: userMetadataError,
    isLoading: isUserMetadataLoading,
  } = useUserMetadataQuery(
    {
      userId: userAlias,
      programId,
    },
    {
      select: (data) => data.userMetadata,
    },
  );

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useUserDetailsQuery(
    {
      userId: userAlias,
      programId,
      businessUnit,
    },
    {
      select: (data) => data.userDetails.userPermission,
    },
  );

  const divisions = user?.divisions ?? [];
  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = useUserPermissionsQuery({
    userId: userAlias,
    programId,
    businessUnit,
    divisions,
  });

  // Redirect to preferences page if we checked that no business unit is set and we have the user alias
  useEffect(() => {
    const isOnPreferencesPage = location.pathname === routePaths.PROFILE_PREFERENCES.path;
    if (isUserPreferencesLoading || isOnPreferencesPage) return;
    if (!userAlias) return;
    if (businessUnit) return;
    startTransition(() => {
      navigate(routePaths.PROFILE_PREFERENCES.path);
    });
  }, [userPreferences, isUserPreferencesLoading, location.pathname, userAlias, navigate]);
  const isLoading =
    isMidwayLoading ||
    isUserLoading ||
    isUserPreferencesLoading ||
    isUserMetadataLoading ||
    isPermissionsLoading ||
    isPending;

  const isError =
    midwayError ||
    userError ||
    userPreferencesError ||
    permissionsError ||
    userMetadataError;

  if (isPending) {
    return <AILoadingScreen messages={['Redirecting to setup...']} />;
  }
  if (isLoading) {
    return <AILoadingScreen messages={initMessages} />;
  }
  function getErrorMessages() {
    const messages = [];
    if (midwayError) {
      console.error(midwayError);
      messages.push('An error while loading midway credentials.');
    }
    if (userError) {
      console.error(userError);
      messages.push('An error while loading user data.');
    }
    if (userPreferencesError) {
      console.error(userPreferencesError);
      messages.push('An error while loading user preferences.');
    }
    if (userMetadataError) {
      console.error(userMetadataError);
      messages.push('An error while loading user metadata.');
    }
    if (permissionsError) {
      console.error(permissionsError);
      if (permissionsError instanceof PermissionsExpiredException) {
        messages.push(
          'Your permissions have expired. Please contact your administrator to renew your permissions.',
        );
      } else {
        messages.push('An error while loading user permissions.');
      }
    }
    return messages;
  }
  if (isError) {
    return (
      <Flashbar
        items={getErrorMessages().map((message) => ({
          type: 'error',
          header: 'Error',
          content: message,
          id: message,
        }))}
      />
    );
  }
  if (!user || !userPreferences || !userMetadata || !permissions || !businessUnit) {
    return (
      <Flashbar
        items={[
          {
            type: 'error',
            header: 'Error',
            content:
              'An error occurred while loading user data. Try again later or contact an administrator.',
            id: 'error',
          },
        ]}
      />
    );
  }

  return (
    <PermissionContext.Provider
      value={{
        permissions: permissions ?? new Set<Permission>(),
        user,
        // We're asserting that the business unit is set in the user preferences
        userPreferences: {
          ...userPreferences,
          profileSettings: {
            ...userPreferences.profileSettings,
            businessUnit: businessUnit,
            businessUnitLocationPreferences:
              userPreferences?.profileSettings?.businessUnitLocationPreferences ?? [],
          },
        },
        userMetadata: userMetadata,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const useUserDetails = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('useUserDetails must be used within a UserProvider');
  }
  return context;
};

export default UserProvider;
